import type { IArticleConfig } from '~/config/article';
import WidgetsCreator from './WidgetsCreator';
import { CommentsListener } from './CommunicationCreator/CommunicationListeners/CommentsListener';
import type { ModalListener } from './CommunicationCreator/CommunicationListeners/ModalListener';
import CommunicationCreator from './CommunicationCreator';
import type { IIframeInterface } from './iframes/Iframe';
import BasePanel from './BasePanel';
import Comments from './iframes/Comments';

class CommentsPanel extends BasePanel {
  private listenerInstance: CommentsListener;
  private modalListenerInstance: ModalListener;
  private loadVuukleSent = false;

  constructor(
    private readonly widgetObject: IIframeInterface,
    private readonly index: string,
    private readonly articleConfig: IArticleConfig,
    private readonly loadVuukleData: any,
    private readonly widgetsCreator: WidgetsCreator
  ) {
    super();
    this.createCommentsAndModalInstance();
  }

  public sendLoadVuukleData(data: any) {
    const interval = setInterval(() => {
      if (this.loadVuukleSent) {
        clearInterval(interval);
        return;
      }
      if (typeof this.listenerInstance !== 'undefined' && typeof this.modalListenerInstance !== 'undefined') {
        this.listenerInstance.postMessage({ loadVuukle: data });
        if (data.settings.theme.color) {
          this.listenerInstance?.setThemeColor(data.settings.theme.color);
        }
        this.modalListenerInstance.postMessage({ loadVuukle: data });
        this.loadVuukleSent = true;
        clearInterval(interval);
      }
    }, 100);
  }

  /**
   * Moves the ad unit from the in-page CW to the comments panel and back
   * @param direction The spot where the ad should be moved
   */
  protected moveAdUnitTo(direction: 'comments-panel' | 'original-spot') {
    const widgetParent = this.widgetObject.iframe.parentElement;
    if (widgetParent) {
      const adContainer =
        direction === 'comments-panel'
          ? widgetParent.querySelector('.vuukle-ads')
          : this.panelContentDiv.querySelector('.vuukle-ads');
      if (adContainer) {
        direction === 'comments-panel' ? this.panelContentDiv.prepend(adContainer) : widgetParent.prepend(adContainer);
        const adElement = adContainer.querySelector('div[id*="div-gpt-ad"]');
        if (adElement) {
          const adId = adElement.id;
          // Refresh the ad unit. The reason we need to do this is because the ad unit iframe loses its state when moved and turns into a blank
          // and we don't want the ad to stay blank for too long
          if (window.vuuklehb && window.vuuklehb.vRefresh) {
            window.vuuklehb.vRefresh(adId, true);
          }
        }
      }
    }
  }

  private async createCommentsAndModalInstance() {
    const commentsInstance = new Comments('v-comments'.concat(this.index), this.articleConfig, this.index);

    const commentsListenerInstance = new CommentsListener(
      commentsInstance,
      this.index,
      this.articleConfig,
      this.widgetsCreator,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined
    );
    this.listenerInstance = commentsListenerInstance;

    const customText = await import(/* webpackChunkName: "cst" */ './CustomText');
    const customTextInstance = new customText.default(
      commentsInstance,
      undefined,
      undefined,
      [],
      undefined,
      this.articleConfig
    );

    const modalIframe = await import(/* webpackChunkName: 'mdi' */ './iframes/Modal');
    const modalIframeInstance = new modalIframe.default(
      'comments-panel-modal-iframe'.concat(this.index),
      this.articleConfig
    );

    const modalListener = await import(
      /* webpackChunkName: "mdl" */ './CommunicationCreator/CommunicationListeners/ModalListener'
    );
    const modalListenerInstance = new modalListener.ModalListener(
      modalIframeInstance,
      this.articleConfig,
      [],
      commentsListenerInstance,
      undefined,
      undefined,
      undefined
    );
    this.modalListenerInstance = modalListenerInstance;
    let isModalDataSent = false;
    let isCommentsDataSent = false;

    commentsListenerInstance.updateModalInstance(modalIframeInstance, modalListenerInstance);
    CommunicationCreator.widgetsOnPageWithThemeSupport[`${this.index}-2`] = {
      modalListener: modalListenerInstance,
      commentsListener: commentsListenerInstance,
    };
    customTextInstance.initializeModal(modalIframeInstance);
    if (!this.loadVuukleSent && typeof this.loadVuukleData !== 'undefined') {
      isModalDataSent = true;
      modalListenerInstance.postMessage({ loadVuukle: this.loadVuukleData });
    }
    modalIframeInstance.loadIframe();

    const div = document.createElement('div');
    div.appendChild(modalIframeInstance.iframe);
    document.body.appendChild(div);

    this.panelContentDiv.appendChild(commentsInstance.iframe);
    if (!this.loadVuukleSent && typeof this.loadVuukleData !== 'undefined') {
      isCommentsDataSent = true;
      commentsListenerInstance.postMessage({ loadVuukle: this.loadVuukleData });
      if (this.loadVuukleData.settings.theme.color) {
        this.listenerInstance?.setThemeColor(this.loadVuukleData.settings.theme.color);
      }
    }

    this.loadVuukleSent = isModalDataSent && isCommentsDataSent;
    commentsInstance.loadIframe();
  }
}

export default CommentsPanel;
